import {
    DropTarget,
    TABLE_FULL_MESSAGE,
    UNSEATED_GROUP_ID,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { TicketDropType } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/dropTypes';
import {
    DropReducer,
    ReducerState,
    StoreAction,
    TicketDropResult,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/types';
import {
    DropTargetPayload,
    SeatingTicket,
    TransformedSeatingGroup,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';
import { indexToSeatNumber } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/helpers';

const ticketDropReducer: DropReducer = (state, action) => {
    const { activeData, overData, selectedTicketIds = [] } = action.payload;

    const targetTicket = validateDropTarget(overData, state.tickets);
    if (!targetTicket) {
        return {
            action: StoreAction.SHOW_ERROR,
            payload: { message: 'Invalid drop target' },
        };
    }

    const ticketIds = extractTicketIds(activeData, selectedTicketIds);
    if (ticketIds.length === 0) {
        return {
            action: StoreAction.SHOW_ERROR,
            payload: { message: 'No tickets selected' },
        };
    }

    const scenario = determineTicketScenario(state, ticketIds, targetTicket);

    switch (scenario.type) {
        case TicketDropType.SAME_TABLE_SWAP:
            return {
                action: StoreAction.SWAP_GUESTS,
                payload: {
                    ticketId1: scenario.sourceTicketId,
                    ticketId2: scenario.targetTicketId,
                },
            };

        case TicketDropType.UNSEATED_TO_TABLE_RESHUFFLE:
        case TicketDropType.TABLE_WITH_ROOM_RESHUFFLE:
            return {
                action: StoreAction.RESHUFFLE_AND_SEAT,
                payload: {
                    ticketIds: scenario.ticketIds,
                    tableId: scenario.tableId,
                    seatNumber: scenario.seatNumber,
                },
            };

        case TicketDropType.TABLE_FULL:
            return {
                action: StoreAction.SHOW_ERROR,
                payload: {
                    message: TABLE_FULL_MESSAGE,
                },
            };

        default:
            return {
                action: StoreAction.SHOW_ERROR,
                payload: { message: 'Invalid drop action' },
            };
    }
};

const determineTicketScenario = (
    state: ReducerState,
    ticketIds: string[],
    targetTicket: SeatingTicket,
): TicketDropResult => {
    if (targetTicket.seating_group_id === UNSEATED_GROUP_ID) {
        return handleUnseatedTargetScenario(state, ticketIds, targetTicket);
    }

    return handleTableTargetScenario(state, ticketIds, targetTicket);
};

const handleUnseatedTargetScenario = (
    state: ReducerState,
    ticketIds: string[],
    targetTicket: SeatingTicket,
): TicketDropResult => {
    if (targetTicket.ticket_group_id) {
        const incompatible = isIncompatibleWithTargetGroup(
            ticketIds,
            targetTicket.ticket_group_id,
            state.tickets,
        );

        if (incompatible) {
            return { type: TicketDropType.INVALID };
        }
    }

    if (ticketIds.length > 1) {
        const adjustedPosition = calculateUnseatedDropPosition(
            ticketIds[0],
            targetTicket,
            state.tickets,
        );
        return createReshuffleAction(ticketIds, UNSEATED_GROUP_ID, adjustedPosition);
    }

    const activeTicket = state.tickets[ticketIds[0]];
    if (!activeTicket) {
        return { type: TicketDropType.INVALID };
    }

    const unseatedGroupAction = getUnseatedGroupAction(state, activeTicket);

    if (unseatedGroupAction) {
        return unseatedGroupAction;
    }

    if (activeTicket.seating_group_id && targetTicket.seat_number !== null) {
        const adjustedPosition = calculateUnseatedDropPosition(
            activeTicket.id,
            targetTicket,
            state.tickets,
        );
        return createReshuffleAction([activeTicket.id], UNSEATED_GROUP_ID, adjustedPosition);
    }

    return { type: TicketDropType.INVALID };
};

const handleTableTargetScenario = (
    state: ReducerState,
    ticketIds: string[],
    targetTicket: SeatingTicket,
): TicketDropResult => {
    const targetTable = validateTargetTable(targetTicket.seating_group_id, state.seatingGroups);
    if (!targetTable) {
        return { type: TicketDropType.INVALID };
    }

    const areAllFromSameTable = checkAllTicketsFromTable(ticketIds, targetTable.id, state.tickets);

    if (ticketIds.length === 1 && areAllFromSameTable) {
        const sourceTicket = state.tickets[ticketIds[0]];
        if (sourceTicket && areAdjacentSeats(sourceTicket, targetTicket)) {
            return {
                type: TicketDropType.SAME_TABLE_SWAP,
                sourceTicketId: ticketIds[0],
                targetTicketId: targetTicket.id,
            };
        }
    }

    if (areAllFromSameTable) {
        return createReshuffleAction(ticketIds, targetTable.id, targetTicket.seat_number);
    }

    const hasTableCapacity = checkTableCapacity(
        targetTable.id,
        ticketIds,
        targetTable.capacity || 0,
        state.tickets,
    );

    if (!hasTableCapacity) {
        return { type: TicketDropType.TABLE_FULL };
    }

    return createReshuffleAction(ticketIds, targetTable.id, targetTicket.seat_number);
};

const getUnseatedGroupAction = (
    state: ReducerState,
    activeTicket: SeatingTicket,
): TicketDropResult | null => {
    if (!activeTicket.ticket_group_id) {
        return null;
    }

    const targetSeat = findNextUnseatedGroupSeat(activeTicket, state.tickets);

    if (targetSeat == null) {
        return null;
    }

    return createReshuffleAction([activeTicket.id], UNSEATED_GROUP_ID, targetSeat);
};

const validateDropTarget = (
    overData: DropTargetPayload,
    tickets: Record<string, SeatingTicket>,
): SeatingTicket | null => {
    const isValidType =
        overData.type === DropTarget.TICKET || overData.type === DropTarget.OCCUPIED_SEAT;

    if (!isValidType || !overData.ticketId) {
        return null;
    }

    return tickets[overData.ticketId] || null;
};

const extractTicketIds = (activeData: DropTargetPayload, selectedTicketIds: string[]): string[] => {
    if (activeData.type === DropTarget.TICKET && activeData.ticketId) {
        return [activeData.ticketId, ...selectedTicketIds.filter(id => id !== activeData.ticketId)];
    }
    return selectedTicketIds;
};

const isIncompatibleWithTargetGroup = (
    ticketIds: string[],
    targetGroupId: string,
    tickets: Record<string, SeatingTicket>,
): boolean => {
    return ticketIds.some(ticketId => {
        const ticket = tickets[ticketId];
        return ticket?.ticket_group_id !== targetGroupId;
    });
};

const validateTargetTable = (
    seatingGroupId: string | null,
    seatingGroups: Record<string, TransformedSeatingGroup>,
): TransformedSeatingGroup | null => {
    if (!seatingGroupId) {
        return null;
    }
    return seatingGroups[seatingGroupId] || null;
};

const checkTableCapacity = (
    tableId: string,
    incomingTicketIds: string[],
    tableCapacity: number,
    tickets: Record<string, SeatingTicket>,
): boolean => {
    const currentOccupants = Object.values(tickets).filter(
        t => t.seating_group_id === tableId && !incomingTicketIds.includes(t.id),
    );

    const finalOccupancy = currentOccupants.length + incomingTicketIds.length;
    return finalOccupancy <= tableCapacity;
};

const checkAllTicketsFromTable = (
    ticketIds: string[],
    tableId: string,
    tickets: Record<string, SeatingTicket>,
): boolean => {
    return ticketIds.every(ticketId => {
        const ticket = tickets[ticketId];
        return ticket?.seating_group_id === tableId;
    });
};

const areAdjacentSeats = (sourceTicket: SeatingTicket, targetTicket: SeatingTicket): boolean => {
    return !!(
        sourceTicket.seat_number &&
        targetTicket.seat_number &&
        Math.abs(sourceTicket.seat_number - targetTicket.seat_number) === 1
    );
};

const findNextUnseatedGroupSeat = (
    activeTicket: SeatingTicket,
    tickets: Record<string, SeatingTicket>,
): number | null => {
    const unseatedGroupMembers = Object.values(tickets)
        .filter(
            t =>
                t.ticket_group_id === activeTicket.ticket_group_id &&
                t.seating_group_id === UNSEATED_GROUP_ID &&
                t.id !== activeTicket.id,
        )
        .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0));

    if (unseatedGroupMembers.length === 0) {
        return null;
    }

    const lastMember = unseatedGroupMembers[unseatedGroupMembers.length - 1];
    return indexToSeatNumber(lastMember.seat_number || 0);
};

const createReshuffleAction = (
    ticketIds: string[],
    tableId: string,
    seatNumber: number | null,
): TicketDropResult => {
    return {
        type:
            tableId === UNSEATED_GROUP_ID
                ? TicketDropType.UNSEATED_TO_TABLE_RESHUFFLE
                : TicketDropType.TABLE_WITH_ROOM_RESHUFFLE,
        ticketIds,
        tableId,
        seatNumber,
    };
};

const calculateUnseatedDropPosition = (
    sourceTicketId: string,
    targetTicket: SeatingTicket,
    tickets: Record<string, SeatingTicket>,
): number | null => {
    if (targetTicket.seat_number == null) {
        return null;
    }

    const sourceTicket = tickets[sourceTicketId];
    const isDraggingDown =
        sourceTicket &&
        sourceTicket.seating_group_id === UNSEATED_GROUP_ID &&
        sourceTicket.seat_number &&
        sourceTicket.seat_number < targetTicket.seat_number;

    return isDraggingDown ? indexToSeatNumber(targetTicket.seat_number) : targetTicket.seat_number;
};

export { ticketDropReducer };
